import React from "react";
import Slider from "react-slick";
import styled, { keyframes } from "styled-components";
import { FaQuoteLeft, FaStar } from "react-icons/fa";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const FullWidthSection = styled.section`
  width: 100%;
  background: linear-gradient(135deg, #f8f9ff, #f0f7ff);
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(90deg, #151c48, #5ab9d3);
  }

  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const SectionContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  position: relative;

  @media (max-width: 992px) {
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    padding: 0 25px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 70px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    margin-bottom: 50px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #151c48;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #5ab9d3, #151c48);
    border-radius: 2px;
  }

  @media (max-width: 992px) {
    font-size: 2.4rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #5f6c84;
  max-width: 700px;
  margin: 30px auto 0;
  line-height: 1.7;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin-top: 25px;
  }
`;

const SliderContainer = styled.div`
  position: relative;
  padding: 20px 0 60px;

  .slick-list {
    margin: 0 -20px;
    padding: 30px 0;
    overflow: visible;
  }

  .slick-slide {
    padding: 0 20px;
    transition: all 0.4s ease;
  }

  .slick-slide.slick-active {
    transform: scale(1);
    opacity: 1;
  }

  .slick-slide:not(.slick-active) {
    opacity: 0.6;
    transform: scale(0.96);
  }
`;

const PremiumFeedbackCard = styled.div`
  padding: 40px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(21, 28, 72, 0.08);
  position: relative;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  animation: ${fadeIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay * 0.1}s;
  opacity: 0;
  border: 1px solid rgba(0, 0, 0, 0.03);
  height: 100%;
  min-height: 380px;
  display: flex !important;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(21, 28, 72, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(90deg, #5ab9d3, #151c48);
    border-radius: 20px 20px 0 0;
  }
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: 30px;
  right: 30px;
  color: rgba(90, 185, 211, 0.08);
  font-size: 5rem;
  z-index: 0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  z-index: 1;
`;

const UserAvatar = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: #eef2f8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-size: 1.8rem;
  font-weight: 700;
  color: #151c48;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: #151c48;
  margin-bottom: 5px;
`;

const UserLocation = styled.p`
  font-size: 0.9rem;
  color: #5ab9d3;
  font-weight: 600;
`;

const RatingContainer = styled.div`
  display: flex;
  margin-top: 8px;
`;

const StarIcon = styled(FaStar)`
  color: ${props => props.filled ? '#FFD700' : '#e0e0e0'};
  margin-right: 3px;
  font-size: 1rem;
`;

const FeedbackText = styled.p`
  font-size: 1.1rem;
  color: #5f6c84;
  line-height: 1.8;
  position: relative;
  z-index: 1;
  flex-grow: 1;
  padding-right: 10px;
`;

const CustomDot = styled.li`
  button {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.active ? '#151c48' : '#d0d5dd'};
    opacity: 1;
    transition: all 0.3s ease;
    margin: 0 6px;
    padding: 0;

    &::before {
      display: none;
    }

    &:hover {
      background: #151c48;
    }
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding-top: 20px;

  ul {
    display: flex;
    gap: 8px;
  }
`;

const DecorationElement = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(90, 185, 211, 0.06), rgba(21, 28, 72, 0.03));
  filter: blur(60px);
  z-index: 1;

  &:nth-child(1) {
    top: -200px;
    left: -200px;
  }

  &:nth-child(2) {
    bottom: -150px;
    right: -150px;
  }
`;

const FeedbackSlider = ({ feedbacks }) => {
  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    arrows: false,
    appendDots: dots => (
      <DotsContainer>
        <ul>
          {dots.map((item, index) => (
            <CustomDot key={index} active={item.props.className === 'slick-active'} />
          ))}
        </ul>
      </DotsContainer>
    ),
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: true
        }
      }
    ]
  };

  return (
    <SliderContainer>
      <Slider {...settings}>
        {feedbacks.map((feedback, index) => (
          <PremiumFeedbackCard key={index} delay={index % 2}>
            <QuoteIcon>
              <FaQuoteLeft />
            </QuoteIcon>
            <UserInfo>
              <UserAvatar>
                {feedback.avatar ? (
                  <img src={feedback.avatar} alt={feedback.name} />
                ) : (
                  feedback.name.charAt(0)
                )}
              </UserAvatar>
              <UserDetails>
                <UserName>{feedback.name}</UserName>
                <UserLocation>{feedback.location}</UserLocation>
                <RatingContainer>
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} filled={i < feedback.rating} />
                  ))}
                </RatingContainer>
              </UserDetails>
            </UserInfo>
            <FeedbackText>{feedback.text}</FeedbackText>
          </PremiumFeedbackCard>
        ))}
      </Slider>
    </SliderContainer>
  );
};

const PremiumFeedbackSection = () => {
  const feedbackData = [
    {
      name: "Carla Rodrigues",
      text: "Com o Realoca Fácil, encontrar consultores especializados para minha mudança foi muito rápido e prático. A plataforma simplificou todo o processo burocrático que antes parecia assustador.",
      rating: 5,
      location: "Lisboa, Portugal",
      avatar: ""
    },
    {
      name: "Lucas Almeida",
      text: "Estava completamente perdido com o processo de relocation até encontrar esta plataforma. Os profissionais conectados superaram todas as expectativas e cuidaram de cada detalhe.",
      rating: 5,
      location: "Porto, Portugal",
      avatar: ""
    },
    {
      name: "Fernanda Souza",
      text: "A intermediação do Realoca Fácil foi fundamental para minha mudança internacional. Economizei tempo e dinheiro com os serviços premium oferecidos pelos parceiros da plataforma.",
      rating: 4,
      location: "Famalicão, Portugal",
      avatar: ""
    },
    {
      name: "Roberto Lima",
      text: "Como executivo relocado, posso afirmar que o Realoca Fácil oferece o mais alto nível de serviço. Minha família e eu fomos atendidos com excelência em todos os aspectos da mudança.",
      rating: 5,
      location: "Leiria, Portugal",
      avatar: ""
    },
    {
      name: "Ana Beatriz",
      text: "Nunca imaginei que realocar uma família inteira poderia ser tão tranquilo. A plataforma nos conectou com especialistas que resolveram desde documentação até adaptação escolar para as crianças.",
      rating: 5,
      location: "Braga, Portugal",
      avatar: ""
    },
    {
      name: "Tiago Martins",
      text: "O diferencial do Realoca Fácil está na curadoria dos profissionais. Todos os serviços contratados através da plataforma foram executados com perfeição e dentro do prazo.",
      rating: 4,
      location: "Lisboa, Portugal",
      avatar: ""
    }
  ];

  return (
    <FullWidthSection>
      <DecorationElement />
      <DecorationElement />
      <SectionContainer>
        <SectionHeader>
          <SectionTitle>Depoimentos de Clientes</SectionTitle>
          <SectionSubtitle>
            Veja o que nossos clientes dizem sobre suas experiências com os serviços de realocação
          </SectionSubtitle>
        </SectionHeader>
        
        <FeedbackSlider feedbacks={feedbackData} />
      </SectionContainer>
    </FullWidthSection>
  );
};

export default PremiumFeedbackSection;